import React from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import * as S from '@/components/Ad/AuthorInfo/AuthorInfo.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import {
  buildWorkingScheduleFallbackString,
  buildWorkingScheduleStatus,
  getUsersAdsHref,
} from '@/components/Ad/AuthorInfo/AuthorInfo.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { AdStatus } from '@/types';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useShopFavorite } from '@/hooks/useShopFavorite';
import { useServerDataContext } from '@/contexts';
import { useResponsive } from '@/hooks/useResponsive';
import { LocationOutlinedIcon } from '@/components/UI/Icons/Icons';
import { formatNumber, setSuffix } from '@/utils/helpers';
import { WarningMessage } from '@/components/Ad/WarningMessage';
import { getVisibleShop } from '@/views/Ads/adShop.helpers';

export const AuthorInfo = () => {
  const isBrowser = typeof window !== 'undefined';
  const { isDesktop } = useResponsive();
  const { t, i18n } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const isAuthor = useIsAuthor();
  const { categoryDetails } = useCurrentCategoryDetails({
    skip: !isBrowser,
  });

  const { isFavorited, toggleFavorite: handleSubscribe } = useShopFavorite({
    shop: adData?.shop ?? undefined,
  });

  if (!adData) {
    return (
      <S.AuthorInfoBox>
        <SkeletonLoader
          dataTestId="author-info-skeleton-loader"
          borderRadius={isDesktop ? 14 : 7}
          height={isDesktop ? '100px' : '18px'}
          width={isDesktop ? '100%' : '150px'}
        />
      </S.AuthorInfoBox>
    );
  }

  const { shop, contact, id, status, user, price } = adData;
  const shopFeatures = adData.features?.shopFeatures ?? shop?.features;
  const isShopSubscribeDisabled = shopFeatures?.subscribeEnabled === false;
  const visibleShop = getVisibleShop(adData);
  const canSubscribeToShop = !isShopSubscribeDisabled;

  const isInfoVisible = isAuthor || status === AdStatus.Published;
  const isWarningVisible =
    !isDesktop &&
    !isAuthor &&
    !visibleShop &&
    (status === AdStatus.Published || status === AdStatus.Expired);
  const isPriceVisible = isDesktop && isAuthor && price !== null && price !== 0;

  const info = {
    name: visibleShop ? visibleShop.name : contact.name,
    avatar: visibleShop ? visibleShop.logo?.url : contact.logo?.url,
    phones: contact.phones,
  };

  const usersAdsHref =
    !visibleShop &&
    user &&
    user.publishedAdsCount > 1 &&
    getUsersAdsHref(user.legacyId);

  const workingScheduleStatus = visibleShop
    ? buildWorkingScheduleStatus(visibleShop, t)
    : null;
  const workingScheduleFallback = visibleShop
    ? buildWorkingScheduleFallbackString(visibleShop)
    : '';

  const shopAddressLink =
    visibleShop?.latitude &&
    visibleShop?.longitude &&
    `https://www.google.com/maps?q=${visibleShop.latitude},${visibleShop.longitude}&ll=${visibleShop.latitude},${visibleShop.longitude}&z=15`;

  return (
    <S.AuthorInfoBox>
      {isInfoVisible && (
        <>
          {isPriceVisible && (
            <S.Price>{formatNumber(price, 2, true)} ₼</S.Price>
          )}
          <S.Main
            {...(visibleShop?.uri
              ? {
                  href: visibleShop.uri,
                  as: CustomLink,
                  dataTestId: 'author-info-main',
                  'data-stat': 'ad-business-ads-count-btn',
                }
              : { as: 'div', 'data-testid': 'author-info-main' })}
          >
            <S.MainInfoContainer>
              <S.AuthorName
                $variant="1"
                data-testid="author-info-name"
                $isShop={!!visibleShop}
              >
                {info.name}
              </S.AuthorName>
              {!!visibleShop && (
                <S.WorkingHours data-testid="author-info-working-schedule">
                  {workingScheduleStatus ? (
                    <>
                      <S.WorkingHoursStatus
                        $isOpen={workingScheduleStatus.isOpen}
                      >
                        {workingScheduleStatus.statusLabel}
                      </S.WorkingHoursStatus>{' '}
                      {workingScheduleStatus.timeLabel}
                    </>
                  ) : (
                    workingScheduleFallback
                  )}
                </S.WorkingHours>
              )}
              {visibleShop && Number(visibleShop.adsCount) >= 0 && (
                <S.Link $variant="3" data-testid="author-info-link">
                  {t(
                    setSuffix(
                      i18n,
                      String(visibleShop.adsCount),
                      'common.n_ads'
                    ),
                    {
                      n: visibleShop.adsCount,
                    }
                  )}
                </S.Link>
              )}
              {usersAdsHref && (
                <S.Link
                  $variant="3"
                  data-testid="author-info-link"
                  as={CustomLink}
                  href={usersAdsHref}
                >
                  {t('pages.ad.users_ads')}
                </S.Link>
              )}
            </S.MainInfoContainer>
            {info.avatar ? (
              <S.Avatar
                data-testid={AdPageTestIds.authorInfo.avatar}
                src={info.avatar}
                alt={info.name}
                width={50}
                height={50}
                loading="lazy"
              />
            ) : (
              <IconBox
                data-testid={AdPageTestIds.authorInfo.defaultAvatar}
                $filename="profile.svg"
                $iconWidth={50}
                $iconHeight={50}
                $width={50}
                $height={50}
              />
            )}
          </S.Main>
          {visibleShop && !isAuthor && (
            <S.ShopActions>
              {canSubscribeToShop && (
                <S.SubscribeShopButton
                  $isFavorited={isFavorited}
                  onClick={handleSubscribe}
                >
                  {isFavorited
                    ? t('shop.added_bookmark_shop')
                    : t('shop.bookmark_shop')}
                </S.SubscribeShopButton>
              )}
              <S.GoToShopButton
                as={CustomLink}
                href={visibleShop.uri}
                data-stat="ad-business-shop-btn"
              >
                {t('pages.ad.go_to_shop')}
              </S.GoToShopButton>
            </S.ShopActions>
          )}
          {(!isDesktop || isAuthor) && (
            <S.PhoneNumbers
              phones={info.phones}
              adId={id}
              $hasBottomPadding={isDesktop && !!shopAddressLink}
            />
          )}

          {isDesktop && !!shopAddressLink && visibleShop?.address && (
            <S.Address>
              <LocationOutlinedIcon />
              <Typography.Body
                $isUnderlined
                $variant="4"
                as={Link}
                target="_blank"
                href={shopAddressLink}
              >
                {visibleShop.address}
              </Typography.Body>
            </S.Address>
          )}
        </>
      )}

      {isWarningVisible && categoryDetails?.warningMessage && (
        <WarningMessage message={categoryDetails.warningMessage} />
      )}
    </S.AuthorInfoBox>
  );
};
